import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { HospitalNames } from '@shared/models/hospitalTypes.model';
import { ErrorService } from '@shared/services/error.service';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { Profile } from '../../models/profile.model';
import { RoomService } from '../../services/room.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-profiles-selection-generation',
  templateUrl: './profiles-selection-generation.component.html',
  styleUrls: ['./profiles-selection-generation.component.scss'],
  standalone: false,
})
export class ProfilesSelectionGenerationComponent implements OnInit, AfterViewChecked, OnChanges {
  private instanceId: string;

  @Input() public profiles: Profile[];

  @Input() public rooms: any[];

  @Input() public withTypes: boolean = false;

  @Input() public title: string = '';

  @Input() public generateBtnTitle: string = '';

  // @Input() public withGroup: boolean = false;

  // @Input() public isGardes: boolean = false;

  @Input() public needs: boolean = false;

  @Input() public showIade: boolean = false;

  @Input() public showAnesth: boolean = false;

  @Input() public showNurse: boolean = false;

  public dateLocale = {
    daysOfWeek: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    firstDay: 1,
    weekLabel: 'S',
  };

  public formGroup: FormGroup | undefined;

  public isWeekVisible = false;

  public linkAnesthNeeds: boolean = false;

  public minDate = new Date();

  public monday = new Date();

  public maxDate: Date = new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000);

  public types = [
    {
      label: 'Supprimer et remplacer',
      value: 'false',
    },
    {
      label: 'Ajouter',
      value: 'true',
    },
  ];

  public generationTypes: any[] = [];

  public groupBloc: boolean = false;

  public groupConsult: boolean = false;

  public withGroup: boolean = false;

  public isGardes: boolean = false;

  public startWeek: number | null = null;

  public availableWeeks: number[] = [];

  public hospitalNames: HospitalNames;

  public accessOptions: AccessOptions | undefined;

  public roomsFormElements: any = [{ label: 'Tout sélectionner', value: 'all', isChecked: true }];

  public profilesFormElements: any = [{ label: 'Tout sélectionner', value: 'all', isChecked: true }];

  public accessOptionsHaveAnesth: boolean = false;

  public accessOptionsHaveIade: boolean = false;

  public positions: any = [];

  public get isProfile(): boolean {
    return this.profiles ? true : false;
  }

  public selectedGenerationTypes: string[] | undefined;

  public nightGenerationTypes: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<ProfilesSelectionGenerationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public formBuilder: FormBuilder,
    public roomService: RoomService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private errorService: ErrorService,
    private router: Router,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.accessOptions = this.userService.getAccessOptionsOfPage(this.router.url);
    this.hospitalNames = this.userService.getHospitalNames();
    this.profiles = data.profiles;

    this.rooms = data.rooms;
    this.withTypes = data.withTypes;
    this.title = data.title;
    this.generateBtnTitle = data.generateBtnTitle;
    this.isWeekVisible = data.showWeekDatepicker || false;
    this.withGroup = data.withGroup;
    this.isGardes = data.isGardes;
    if (data.cycleWeeks) {
      this.availableWeeks = data.cycleWeeks;
    }
    if (this.rooms) {
      this.selectAllRoomsChange({ checked: true });
    }
    this.monday = this.getNextMonday(this.minDate);
  }

  getNextMonday(date: Date): Date {
    const nextMonday = new Date(date);
    const day = nextMonday.getDay(); // 0 (Sun) -> 6 (Sat)

    // Days until next Monday
    const daysUntilNextMonday = (8 - day) % 7 || 7;
    nextMonday.setDate(nextMonday.getDate() + daysUntilNextMonday);
    nextMonday.setHours(0, 0, 0, 0); // start of Monday
    return nextMonday;
  }

  async ngOnInit() {
    if (this.withGroup || this.isGardes) {
      this.accessOptionsHaveAnesth = await this.userService.doesAccessOptionHaveAnesthesistes(this.accessOptions);
      this.accessOptionsHaveIade = await this.userService.doesAccessOptionHaveIades(this.accessOptions);
      await this.setAccessOptions();
    }

    if (this.data.needs) {
      this.needs = this.data.needs;
    }

    this.data?.gardeNeedsTables?.forEach((gardeNeedsTable: any) => {
      if (gardeNeedsTable.formationsGroup.displayName && gardeNeedsTable.formationsGroup.typeMode) {
        this.nightGenerationTypes.push({
          label: gardeNeedsTable.formationsGroup.displayName,
          value: gardeNeedsTable.formationsGroup.typeMode,
          isChecked: false,
          isDisabled: false,
        });
      }
    });

    if (this.isProfile) {
      this.profiles.forEach((profile: Profile) => (profile.checked = true));
    }
    this.makeForm();
    if (this.withGroup) {
      this.onGenerationTypesChange();
    }
  }

  ngOnChanges(_changes: SimpleChanges) {
    if (this.formGroup?.get('startDate')?.value > this.formGroup?.get('endDate')?.value) {
      this.formGroup?.get('endDate')?.reset();
    }

    this.formGroup?.get('cycleStartWeek')?.reset();
  }

  handleSelectStartWeek(weekNumber: number) {
    this.formGroup?.patchValue({ cycleStartWeek: weekNumber });
  }

  makeForm(): void {
    if (this.withGroup || this.isGardes) {
      if (this.nightGenerationTypes.length > 0) {
        // Besoins de garde
        this.formGroup = this.formBuilder.group({
          startDate: ['', [Validators.required]],
          endDate: ['', [Validators.required]],
          type: ['true', [Validators.required]],
          cycleStartWeek: [this.availableWeeks.length > 1 ? '' : 1, [Validators.required]],
        });

        if (this.nightGenerationTypes.length > 1) {
          this.formGroup.addControl('generationTypes', this.formBuilder.control('', Validators.required));
        }
      } else {
        // Besoins d'anesthésie
        const positionsList = [
          ...((this.accessOptions?.seniority?.length ?? 0) > 0
            ? this.accessOptions!.seniority!.map((seniority: string) => seniority.toLowerCase())
            : []),
          ...(this.accessOptionsHaveIade ? ['iade'] : []),
        ];

        this.formGroup = this.formBuilder.group({
          startDate: ['', [Validators.required]],
          endDate: ['', [Validators.required]],
          type: ['true', [Validators.required]],
          generationTypes: [['bloc', 'consultation']],
          cycleStartWeek: [''],
          rooms: [''],
          profiles: [''],
          positions: [positionsList],
        });
      }
    } else {
      // TVO et roulements
      this.formGroup = this.formBuilder.group({
        startDate: ['', [Validators.required]],
        endDate: ['', [Validators.required]],
        type: ['true', [Validators.required]],
        cycleStartWeek: [this.availableWeeks.length > 1 ? '' : 1, [Validators.required]], // to check
        rooms: [''],
        profiles: [''],
      });
    }

    this.startWeek = this.availableWeeks.length === 1 ? this.availableWeeks[0] : null;

    if (this.formGroup?.get('startDate')) {
      this.subscriptionManager
        .createRequest(this.formGroup.get('startDate')!.valueChanges, 'startDateForm', this.instanceId)
        .subscribe({
          next: (x: Date) => {
            this.minDate = x;
            // maxDate is startDate + 1 year
            this.maxDate = new Date(new Date(x).getTime() + 365 * 24 * 60 * 60 * 1000);
          },

          error: (error: any) => {
            this.errorService.handleError(error);
          },
        });
    }

    if (Array.isArray(this.rooms)) {
      for (const room of this.rooms) {
        this.roomsFormElements.push({
          label: (this.hospitalNames.showRoomNameOnRooms ? this.hospitalNames.roomsName + ' ' : '') + room.roomNumber,
          isChecked: true,
          value: room._id,
        });
      }

      if (this.data.populationRooms) {
        this.roomsFormElements = this.roomsFormElements.filter(
          (room: any) => this.data.populationRooms.includes(room.value) || room.value === 'all'
        );
      }
    }

    if (Array.isArray(this.profiles)) {
      for (const profile of this.profiles) {
        this.profilesFormElements.push({
          label: `${this.capitalize(profile?.firstName || '')} ${this.capitalize(profile?.lastName || '')} - ${
            profile?.position || ''
          }`,
          isChecked: profile.checked,
          value: profile._id,
        });
      }
    }

    const selectedRoomIds = this.roomsFormElements.filter((r: any) => r.isChecked).map((r: any) => r.value);

    this.formGroup?.get('rooms')?.setValue(selectedRoomIds, { emitEvent: true });
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  toggleAnesthNeeds() {
    this.linkAnesthNeeds = !this.linkAnesthNeeds;
  }

  generate() {
    let generationType,
      positions = [];

    if (this.selectedGenerationTypes) {
      if (this.selectedGenerationTypes.length === 1) {
        generationType = this.selectedGenerationTypes[0];
      } else if (this.selectedGenerationTypes.length === 2 && !this.isGardes) {
        generationType = 'blocConsult';
      } else if (this.selectedGenerationTypes.length === 2 && !this.isGardes) {
        generationType = 'anesthIades';
      }
    }

    if (this.formGroup?.value.positions) {
      positions = this.formGroup?.value.positions;
    }

    const result: any = {
      startDate: this.formGroup?.value.startDate,
      endDate: this.formGroup?.value.endDate,
    };

    if (this.isGardes) {
      // Génération des gardes
      result.type = this.formGroup?.value.type;
      result.toGenerate =
        this.nightGenerationTypes.length > 1
          ? this.formGroup?.get('generationTypes')?.value
          : [this.nightGenerationTypes[0].value];
      result.startingWeekNumber = this.formGroup?.value.cycleStartWeek;
    } else if (this.withGroup) {
      if (!this.roomsFormElements || this.roomsFormElements.length === 0) {
        // Génération des besoins anesthésistes (Consultation )
        result.generationType = 'consult';
      } else {
        // Génération des besoins anesthésistes
        result.generationType = generationType;
        result.concernedRooms = this.roomsFormElements
          .filter((room: any) => room.isChecked && room.value !== 'all')
          .map((room: any) => room.value);
        result.startingWeekNumber = this.formGroup?.value.cycleStartWeek;
        result.positions = positions;
      }
    } else if (!this.isProfile) {
      // Génération des vacations
      result.concernedRooms = this.roomsFormElements
        .filter((room: any) => room.isChecked && room.value !== 'all')
        .map((room: any) => room.value);
      result.startingWeekNumber = this.formGroup?.value.cycleStartWeek;

      if (this.linkAnesthNeeds !== undefined) {
        result.linkAnesthNeeds = this.linkAnesthNeeds;
      }
    } else {
      // Génération des calendars
      result.profiles = this.profilesFormElements
        .filter((profile: any) => profile.isChecked && profile.value !== 'all')
        .map((profile: any) => profile.value);
      result.startingWeekNumber = this.formGroup?.value.cycleStartWeek;
    }
    this.dialogRef.close(result);
  }

  close() {
    this.dialogRef.close();
  }

  selectAllRoomsChange(checkbox: { checked: boolean }) {
    this.rooms.forEach((room: any) => (room.checked = checkbox.checked));
  }

  capitalize(s: string): string {
    const text = s.toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  chosenDate(event: any) {
    const startDate = event.startDate ? event.startDate.toDate() : null;
    const endDate = event.endDate ? event.endDate.toDate() : null;

    this.formGroup?.patchValue({
      startDate: startDate,
      endDate: endDate,
    });
  }

  onRoomChange(event: MatCheckboxChange, room: any): void {
    room.isChecked = event.checked;

    if (room.label === 'Tout sélectionner') {
      this.roomsFormElements.forEach((r: any) => {
        r.isChecked = event.checked;
      });
    }

    const selectedRoomIds = this.roomsFormElements.filter((r: any) => r.isChecked).map((r: any) => r.value);

    this.formGroup?.get('rooms')?.setValue(selectedRoomIds, { emitEvent: true });
  }

  onProfileChange(event: MatCheckboxChange, profile: any): void {
    profile.isChecked = event.checked;

    if (profile.label === 'Tout sélectionner') {
      this.profilesFormElements.forEach((r: any) => {
        r.isChecked = event.checked;
      });
    }

    const selectedProfilesIds = this.profilesFormElements.filter((r: any) => r.isChecked).map((r: any) => r.value);

    this.formGroup?.get('rooms')?.setValue(selectedProfilesIds, { emitEvent: true });
  }

  onGenerationTypesChange() {
    this.selectedGenerationTypes = ['bloc', 'consultation'];
    if (this.formGroup?.get('generationTypes')) {
      this.subscriptionManager
        .createRequest(this.formGroup.get('generationTypes')!.valueChanges, 'generationTypesForm', this.instanceId)
        .subscribe({
          next: (val: string[]) => {
            this.selectedGenerationTypes = val;
            if (val.find((e: string) => e === 'bloc')) {
              this.groupBloc = true;
            } else {
              this.groupBloc = false;
            }
          },

          error: (error: any) => {
            this.errorService.handleError(error);
          },
        });
    }
  }

  async setAccessOptions() {
    const formationsGroups = await this.userService.getFormationsGroupsOfAccessOption(
      this.accessOptions?.positions ?? []
    );

    const workScopes = Array.from(new Set(formationsGroups.flatMap((fg: any) => fg.workScopes || [])));

    if (this.accessOptions?.seniority?.includes('Senior')) {
      this.positions.push({
        label: 'Sénior',
        value: 'senior',
        isChecked: false,
      });
    }

    if (this.accessOptions?.seniority?.includes('Junior')) {
      this.positions.push({
        label: 'Junior',
        value: 'junior',
        isChecked: false,
      });
    }

    if (this.accessOptions?.seniority?.includes('Interne')) {
      this.positions.push({
        label: 'Interne',
        value: 'interne',
        isChecked: false,
      });
    }

    if (this.accessOptionsHaveIade) {
      this.positions.push({
        label: 'Iade',
        value: 'iade',
        isChecked: false,
      });
    }

    if (workScopes.includes('bloc')) {
      this.generationTypes.push({
        label: this.hospitalNames.blocName,
        value: 'bloc',
        isChecked: true,
        isDisabled: false,
      });
    }

    if (workScopes.includes('consultation')) {
      this.generationTypes.push({
        label: this.hospitalNames.consultationName,
        value: 'consultation',
        isChecked: true,
        isDisabled: false,
      });
    }
  }
}
